'use client';
import * as DS from '@aurorasolar/ds';
import { IGridItemSize } from '@aurorasolar/ds';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useTransition } from 'react';

import { PostSummary } from '../actions';

import { PostPreview, PostPreviewSkeleton } from './PostPreview';

type Props = {
  loading: boolean;
  onNavigate: (slug: string) => void;
  pageSize: number;
  posts: PostSummary[];
  size?: DS.IResponsiveProperty<IGridItemSize>;
};

const defaultSize = { _: 'full', md: 'half', xl: 'third' } as DS.IResponsiveProperty<IGridItemSize>;

export const PostList = ({ loading, onNavigate, posts = [], pageSize, size = defaultSize }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isPending, startTransition] = useTransition();

  const handlePageChange = useCallback(
    (newPage: number) => {
      startTransition(() => {
        const newParams = toQueryString(searchParams, { page: newPage + 1 });
        router.push(newParams || pathname);
        router.refresh();
      });
    },
    [router, pathname, searchParams]
  );

  const breakpoint = DS.useBreakpoint();
  const totalPages = Math.ceil(posts.length / pageSize);
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const startIndex = (page - 1) * pageSize;

  const paginatedPosts = posts.slice(startIndex, startIndex + pageSize);

  // Handle invalid page numbers
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      router.push(toQueryString(searchParams, { page: totalPages }));
    }
  }, [page, router, totalPages, searchParams]);

  if (loading || isPending) {
    return (
      <>
        <DS.Grid columnGap={{ _: 0, md: 8 }} rowGap={8}>
          {[...Array(3)].map((_, i) => (
            <DS.GridItem
              alignItems="stretch"
              display={{ _: i >= 1 ? 'none' : 'flex', md: 'flex' }}
              key={i} // eslint-disable-line react/no-array-index-key
              size={size}
            >
              <PostPreviewSkeleton />
            </DS.GridItem>
          ))}
        </DS.Grid>

        <DS.Divider my={6} />

        <DS.Flex alignItems="center">
          <DS.Pagination
            currentPage={page - 1}
            handlePageChange={handlePageChange}
            key={String(breakpoint)}
            numberOfPages={totalPages}
            numberOfResults={paginatedPosts.length}
            numberOfResultsPerPage={pageSize}
            renderResultHintText={() => ''}
          />
        </DS.Flex>
      </>
    );
  }

  if (!paginatedPosts.length) {
    return (
      <DS.Flex alignItems="center" justifyContent="center">
        No posts found
      </DS.Flex>
    );
  }

  return (
    <>
      <DS.Grid columnGap={{ _: 0, md: 8 }} rowGap={8}>
        {paginatedPosts.map((post) => (
          <DS.GridItem alignItems="stretch" display="flex" key={post.slug} size={size}>
            <PostPreview onNavigate={onNavigate} post={post} />
          </DS.GridItem>
        ))}
      </DS.Grid>

      <DS.Divider my={6} />

      <DS.Flex alignItems="center">
        {paginatedPosts.length ? (
          <DS.Pagination
            currentPage={page - 1}
            handlePageChange={handlePageChange}
            key={String(breakpoint)}
            numberOfPages={totalPages}
            numberOfResults={paginatedPosts.length}
            numberOfResultsPerPage={pageSize}
            renderResultHintText={() => ''}
          />
        ) : null}
      </DS.Flex>
    </>
  );
};

export type SearchParams = { affid?: string; page: number; search: string };

export const toQueryString = (currentParams: URLSearchParams, newParams: Partial<SearchParams> = {}) => {
  const params: Partial<SearchParams> = { ...Object.fromEntries(currentParams.entries()), ...newParams };
  const paramsString = Object.entries(params)
    .filter(([key, value]) => value && (key !== 'page' || Number(value) > 1))
    .map(([key, value]) => [key, value.toString()]);

  const queryString = new URLSearchParams(paramsString).toString();

  return queryString ? `?${queryString}` : '';
};
